import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs/Subject';
import * as immutable from 'immutable';
import { Observable } from 'rxjs/Observable';
import { Queries } from '@ispm/shared/utils/Queries';
import { QueryTerm } from '@ispm/shared/classes/query-term';
import { Workflow } from '@ispm/core/interfaces/workflow.interface';
import { CoreActionCreatorService } from '@ispm/core/services/core-action-creator.service';
import { isEmpty } from 'lodash';
import { WorkflowConfigService } from '@ispm/workflow/services/workflow-config.service';
import { IWorkflowRequest } from '@ispm/workflow/interfaces/workflow-request.interface';
import { ItherapyMatch } from '../../interfaces/therapy-match.interface';
import { IclinicalTrials } from '../../interfaces/clinical-trials.interface';
import { TherapiesService } from '@ispm/therapies/services/therapies.service';
import { TherapiesActionCreatorService } from '@ispm/therapies/services/therapies-action-creator.service';
import { filter, map, take, tap } from 'rxjs/operators';
import { TestService } from '@ispm/dashboard/test/services/test.service';
import { AuthenticationService } from '@ispm/user/services/authentication.service';
import { WorkflowService } from '@ispm/workflow/services/workflow.service';
import {buttonsNamesEnum} from "@ispm/test/constants/Tabs.constants";
import {ActivatedRoute} from "@angular/router";

@Component({
  selector: 'gen-therapy-prioritize',
  templateUrl: './therapy-prioritize.component.html',
  styleUrls: ['./therapy-prioritize.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TherapyPrioritizeComponent implements OnInit, OnDestroy {
  public selectedDrugs: any = Observable.from([]);
  public isPrioritizeStep: boolean = false;
  public userPassReviewLimit: boolean;
  public workflowInputs$: Observable<any>;
  public therapies: Observable<any>;
  public opinion: Observable<any>;
  public uncharacterizedTherapies: Observable<any>;
  public notSentForInterpretationTherapies: Observable<any>;
  public providers$: Observable<string[]>;
  public reviews: any[];
  public currentReview: any;
  public revisionId: string;
  private workflowId: string;
  private testId: string;
  public componentDestroyer$ = new Subject();
  public caseStatus: Observable<boolean>;
  public trials$: Observable<{ [AberrationId: string]: IclinicalTrials[]}>;
  public allowManual: boolean = true;
  public allowTherapyFreeText: boolean = false;
  public targetMaxLength: number = 30;
  public isUiManualAberrationCurationStep: boolean = false;
  public isPerformer: number;
  public loaded: boolean = false;
  public reflexMessage: string = '';

  public activeJobName: Observable<any>;
  private workflowRequest: Observable<IWorkflowRequest> = this.getWorkflowRequest();
  private componentDestroyed: boolean = false;
  private userRole: string;
  private currentStep: any;
  private userId: string;
  private allTherapies: boolean = false;
  public immunotherapyConfig: any;


  constructor(private activatedRoute: ActivatedRoute,
              private changeDetector: ChangeDetectorRef,
              private workflowService: WorkflowService,
              private authService: AuthenticationService,
              private testService: TestService,
              private therapyService: TherapiesService,
              private CoreAC: CoreActionCreatorService,
              private workflowConfigService: WorkflowConfigService,
              private therapiesAC: TherapiesActionCreatorService) {
    this.revisionId = activatedRoute.parent.snapshot.params.revisionId;
    this.workflowId = activatedRoute.parent.snapshot.params.testId;
    this.testId = activatedRoute.parent.snapshot.params.testId;
    this.userId = authService.getUser().info.id;
    this.userRole = authService.getUser().role;
    this.activeJobName = this.workflowRequest.map((data: IWorkflowRequest) => {
      return data.activeRevision === this.revisionId
        ? data.activeJobName
        : 'None';
    });
    this.workflowInputs$ = this.workflowRequest
    .filter(() => !!this.testService.testConfiguration)
      .map((data: IWorkflowRequest) => {
        this.setImmunotherapy();

        const activeJob = data.activeRevision === this.revisionId ? data.activeJob : null;
        this.currentStep = Workflow.getCurrentStep(Workflow.getRevision(data.revisions, this.revisionId), activeJob);

      // Checks the ManualCurationAvailable property against the "TestConfiguration" table StepType
      this.isUiManualAberrationCurationStep = this.currentStep &&
        this.testService.testConfiguration.hasOwnProperty(this.currentStep.type) &&
        this.testService.testConfiguration[this.currentStep.type].hasOwnProperty('manualCurationAvailable') &&
        this.testService.testConfiguration[this.currentStep.type].manualCurationAvailable;

      this.allTherapies = this.testService.testConfiguration.therapyMatch &&
        this.testService.testConfiguration.therapyMatch.allTherapies;

      this.loaded = true;

      if (this.testService.testConfiguration.therapyMatch) {
        this.allowManual = this.testService.testConfiguration.therapyMatch.allowManualTherapies == null
          ? true
          : this.testService.testConfiguration.therapyMatch.allowManualTherapies;
        this.allowTherapyFreeText = this.testService.testConfiguration.therapyMatch.allowTherapyFreeText;
        this.targetMaxLength = this.testService.testConfiguration.therapyMatch.targetMaxLength || 30;
      }

      return this.currentStep && this.currentStep.inputs ? this.currentStep.inputs : [];
    });

    this.selectedDrugs = this.therapiesAC.getState('userReview')
      .filter(value => !!value)
      .pluck('therapiesIndex')
      .map((value: any) => value.toArray());

  }


  private setImmunotherapy() {
    this.immunotherapyConfig = this.testService.testConfiguration.molecularFindings && this.testService.testConfiguration.molecularFindings.showOnTherapyTab
    && this.testService.testConfiguration.requiredAnnotations.find(annotation => annotation.type && annotation.type.toLowerCase() === 'immunotherapy');
  }

  ngOnInit() {
    this.CoreAC.setLoader(true);
    this.currentReview = this.therapiesAC.getState('userReview').filter(userReview => !!userReview);
    this.caseStatus = this.currentReview.pluck('caseStatus');

    this.setTherapiesSubscriber();
    this.setUncharacterizedTherapiesSubscriber();
    this.setNotSentForInterpretationTherapiesSubscriber();
    this.setProvidersSubscriber();
    this.setTrialsSubscriber();
    this.setOpinionSubscriber();
    this.setReviews();
  }

  private getWorkflowRequest() {
    return this.workflowConfigService.getActiveWorkflow()
      .takeUntil(this.componentDestroyer$.asObservable())
      .filter(workflow => !!workflow)
      .map(currentWorkflow => {
        for (const reference of currentWorkflow.references) {
          if (reference.type.toLowerCase() === 'practitioner') {
            let value;
            try {
              value = JSON.parse(reference.value);
            } catch (e) {
              continue;
            }
            if (value.practType === 'performer') {
              if (value.practId === this.userId) {
                this.isPerformer = 1;
              }
              else if (value.practId.toLowerCase() === 'none') {
                this.isPerformer = 2;
 }
              else {
                this.isPerformer = 0;
 }
              break;
            }
          }
        }
        return currentWorkflow;
      });
  }

  private setProvidersSubscriber() {
    this.providers$ = this.therapiesAC.getState('providers');
  }

  private setOpinionSubscriber() {
    this.opinion = this.therapiesAC.getOpinion()
      .pipe(
        filter(value => !!value),
        tap(() => this.CoreAC.setLoader(false)),
        map((value) => {
          return value;
        })
      );
  }

  private setTherapiesSubscriber() {
    this.therapies = this.therapiesAC.getState('therapies').pipe(
      filter((value: immutable.Map<string, any>) => !!value),
      tap(() => this.CoreAC.setLoader(false))
    );
  }

  private setUncharacterizedTherapiesSubscriber() {
    this.uncharacterizedTherapies = this.therapiesAC.getState('uncharacterizedTherapies')
      .filter((value: Array<ItherapyMatch>) => (value && value.length > 0))
      .do(() => this.CoreAC.setLoader(false));
  }

  private setNotSentForInterpretationTherapiesSubscriber() {
    this.notSentForInterpretationTherapies = this.therapiesAC.getState('notSentForInterpretationTherapies')
      .filter((value: Array<ItherapyMatch>) => (value && value.length > 0))
      .do(() => this.CoreAC.setLoader(false));
  }

  private setTrialsSubscriber() {
    this.trials$ = this.therapiesAC.getTrials().pipe(
      map(trials => TherapiesActionCreatorService.groupTrialsByContextType(trials)),
    );
  }

  private setReviews() {
    let reviewsResponse;
    this.therapiesAC.getReviews()
      .takeUntil(this.componentDestroyer$.asObservable())
      .takeUntil(this.workflowConfigService.workflowChanged())
      .flatMap((reviewsRes: Array<any>) => {
        reviewsResponse = reviewsRes || [];
        return this.workflowInputs$;
      })
      .flatMap((inputs) => {
        return this.workflowRequest;
      })
      .subscribe((currentWorkflow: IWorkflowRequest) => {
        this.reviews = reviewsResponse;
        let finalStep = false, requiredReviews, signOffRole, tmpInput, amountOfReviewsAllowed;
        const currentRevision = Workflow.getRevision(currentWorkflow.revisions, this.revisionId);
        this.therapyService.getReflexMessageForUi(currentRevision.id)
          .pipe(take(1))
          .subscribe(val => {
            this.reflexMessage = isEmpty(val) ? '' : val;
            this.changeDetector.markForCheck();
          });
        if (Workflow.checkCurrentStatus(currentRevision, ['final review', 'test completed'])) {
          this.testService.updateButtonState(buttonsNamesEnum.SIGN_OFF_HIDE, 'signoff');
          finalStep = true;
        }
        if (this.currentStep && this.currentStep.type.toLowerCase() === 'interpretationresults') {
          tmpInput = Workflow.getInput(this.currentStep.inputs, 'requiredReviews');
          requiredReviews = !!tmpInput && tmpInput.value ? <string> tmpInput.value : 0;
          TherapiesService.REQUIRED_REVIEWS = parseInt(<string> requiredReviews);
          tmpInput = Workflow.getInput(this.currentStep.inputs, 'signOffRole');
          signOffRole = !!tmpInput && tmpInput.value ? tmpInput.value.toLowerCase() : 'pathologist';
          tmpInput = Workflow.getInput(this.currentStep.inputs, 'amountOfReviewsAllowed');
          amountOfReviewsAllowed = !!tmpInput && tmpInput.value ? tmpInput.value : '-1';
          tmpInput = Workflow.getInput(this.currentStep.inputs, 'priorityAvailable');
          this.isPrioritizeStep = tmpInput && tmpInput.value !== 'false';
        }
        const query = [new QueryTerm('resourceType', 'Practitioner'), new QueryTerm('reference', this.userId)];
        this.userPassReviewLimit = (amountOfReviewsAllowed != -1 && reviewsResponse.filter(review => Queries.findByProperty(review, 'context', query)).length > amountOfReviewsAllowed);
        const isLastReview = reviewsResponse.length > 0
          ? reviewsResponse[reviewsResponse.length - 1]['context']
            .find(context => Queries.findByProperty(context, 'resourceType', 'Practitioner')).reference === this.userId
          : false;
        if (!finalStep && isLastReview && signOffRole === this.userRole.toLowerCase() && reviewsResponse.length >= requiredReviews) {
          this.testService.updateButtonState(buttonsNamesEnum.SIGN_OFF, 'signoff');
        }
        if (this.isPerformer !== 0) {
          this.therapiesAC.acceptCaseUIHandler(this.isPerformer === 1, this.revisionId);
        }
        if (!this.componentDestroyed) { this.changeDetector.detectChanges(); }
      });
  }

  ngOnDestroy(): void {
    this.componentDestroyer$.next();
    this.componentDestroyer$.complete();
    this.componentDestroyed = true;
  }

}
